import React, { useState, useEffect } from "react";
import Button from "web/react/components/button/button";
import LogotypeCheckbox, { Brand } from "web/react/components/logotype-checkbox/logotype-checkbox";
import GenericModal from "web/react/components/generic-modal/generic-modal";
import { gettext, ngettext } from "web/script/modules/django-i18n";
import analytics from "web/script/analytics/analytics";
import styles from "./discover-modal.css";

interface BrandsArray {
    brands: string[];
}

interface DiscoverModalProps {
    currentBrandName: string;
    relatedBrands: Brand[];
    open: boolean;
    onAdd: () => void;
    onClose: (BrandsArray) => void;
    onBrandClick: (string) => void;
}

function DiscoverModal({
    currentBrandName,
    relatedBrands,
    open,
    onAdd,
    onClose,
    onBrandClick,
}: DiscoverModalProps): JSX.Element {
    let [brandSlugs, setBrandSlugs] = useState<string[]>([]);
    let [buttonText, setButtonText] = useState<string>(
        gettext("feed.brand_personalise.discover_brand.add_count_cta", { count: 0 })
    );

    function handleBrandClick(brand): void {
        let aleadyClicked = brandSlugs.includes(brand);
        if (!aleadyClicked) {
            setBrandSlugs([...brandSlugs, brand]);
        } else {
            let index = brandSlugs.indexOf(brand);

            if (index > -1) {
                brandSlugs.splice(index, 1);
            }

            setBrandSlugs([...brandSlugs]);
        }

        onBrandClick(brand);
        analytics.event("add-brand-button", "clicked", "feeds-discover");
    }

    function handleClose(brands): void {
        let temp = [...brands];
        setBrandSlugs([]);
        onClose(temp);
        analytics.event("dismiss", "clicked", "feeds-discover");
    }

    function handleAdd(): void {
        setBrandSlugs([]);
        onAdd();
        analytics.event("add-more-button", "clicked", "feeds-discover");
    }

    let nbrSelected = relatedBrands.filter((brand) => {
        return brand.checked === true;
    }).length;

    useEffect(() => {
        const translatedStr = ngettext(
            "feed.brand_personalise.discover_brand.add_count_cta",
            "feed.brand_personalise.discover_brand.add_count_cta_pl",
            nbrSelected,
            {
                count: nbrSelected,
            }
        );
        setButtonText(translatedStr);
    }, [nbrSelected]);

    return (
        <GenericModal fullscreen={true} isOpen={open} onClose={(): void => handleClose(brandSlugs)}>
            <div className={styles.container}>
                <div className={styles.scrollArea}>
                    <h1
                        className={styles.title}
                        dangerouslySetInnerHTML={{
                            __html: gettext("feed.brand_personalise.discover_brand.title"),
                        }}
                    />
                    <p
                        className={styles.info}
                        dangerouslySetInnerHTML={{
                            __html: gettext(
                                "feed.brand_personalise.discover_brand.based_on.label",
                                { designer_name: currentBrandName }
                            ),
                        }}
                    />
                    <div className={styles.brands}>
                        {relatedBrands.map((brand, index) => {
                            let slug = brand.slug;
                            if (index < 9) {
                                return (
                                    <div
                                        key={`discover-modal-${brand.image_url}`}
                                        className={styles.brand}
                                    >
                                        <LogotypeCheckbox
                                            onChange={(): void => handleBrandClick(slug)}
                                            {...brand}
                                        />
                                    </div>
                                );
                            }
                        })}
                    </div>
                </div>
                <div className={styles.bottom}>
                    <div className={styles.image} />
                    <div className={styles.button}>
                        <Button
                            className={""}
                            styleType={"cta"}
                            fullHeight={false}
                            fullWidth={true}
                            openInNewTab={false}
                            disabled={false}
                            onClick={handleAdd}
                        >
                            {buttonText}
                        </Button>
                    </div>
                </div>
            </div>
        </GenericModal>
    );
}

export default DiscoverModal;
